/**
 * CloneMatchModal — Clone selected rows from a previous match into the current fixture.
 *
 * Lets clubs pick which rows to carry over from another fixture view.
 * Filters source matches by venue (MCG → MCG, Marvel → Marvel).
 * Prevents accidental carry-over of match-specific numbered content.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { getVenueFromViewName } from "../../constants/columns";

const ASSET_FIELDS = [
  "bigGfxVideosId", "cornerGfxVideosId", "ledAllTypesId",
  "bigTvcId", "cornerTvcId", "iptvGfxId",
  "mcgTiledLedId", "marvelTiledLedId", "superFullId",
  "super169Id", "superSideId", "mcgInterchangeId",
  "marvelInterchangeId", "marvel360Id", "mcg360Id",
];

export default function CloneMatchModal({
  isOpen,
  onClose,
  dynamicViews,
  currentViewName,
  api,
  fixtureForWrites,
  defaultStatus,
  editorName,
  onComplete,
  isTeam,
  viewNameToFixtureKey,
  onSyncRow,
}) {
  const [sourceView, setSourceView] = useState("");
  const [includeAssets, setIncludeAssets] = useState(true);
  const [includeInstructions, setIncludeInstructions] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  // ─── Row selection state ───
  const [sourceRows, setSourceRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ─── Venue filtering: only show same-venue matches ───
  const currentVenue = useMemo(() => getVenueFromViewName(currentViewName), [currentViewName]);

  const sourceOptions = useMemo(() => {
    return (dynamicViews || []).filter(v => {
      if (v === currentViewName) return false;
      const vVenue = getVenueFromViewName(v);
      // If current venue is known, only show same venue
      if (currentVenue !== "ALL" && vVenue !== "ALL") {
        return vVenue === currentVenue;
      }
      return true;
    });
  }, [dynamicViews, currentViewName, currentVenue]);

  // ─── Fetch source rows when source view changes ───
  const fetchSourceRows = useCallback(async (view) => {
    if (!view || !api) return;
    setLoadingRows(true);
    setLoadError(null);
    setSourceRows([]);
    setSelectedIds(new Set());
    try {
      const fixtureKey = viewNameToFixtureKey(view) || "";
      const data = await api.fetch(fixtureKey, { viewName: view });
      if (!data || data.length === 0) {
        setSourceRows([]);
        setLoadError("No rows found in this match.");
        return;
      }
      // Sort by category then cue
      const sorted = [...data].sort((a, b) => {
        const catCmp = String(a.category || "").localeCompare(String(b.category || ""));
        if (catCmp !== 0) return catCmp;
        return (a.cue || 0) - (b.cue || 0);
      });
      setSourceRows(sorted);
      // Default: none selected (single row clone)
      setSelectedIds(new Set());
    } catch (err) {
      console.error("[CloneMatch] Failed to fetch source rows:", err);
      setLoadError("Failed to load rows: " + (err.message || err));
    } finally {
      setLoadingRows(false);
    }
  }, [api, viewNameToFixtureKey]);

  // Fetch when source view changes
  useEffect(() => {
    if (sourceView) {
      fetchSourceRows(sourceView);
    } else {
      setSourceRows([]);
      setSelectedIds(new Set());
      setLoadError(null);
    }
  }, [sourceView, fetchSourceRows]);

  // ─── Group rows by category for display ───
  const groupedRows = useMemo(() => {
    const map = new Map();
    sourceRows.forEach(row => {
      const cat = String(row.category || "Uncategorised").trim();
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(row);
    });
    return map;
  }, [sourceRows]);

  if (!isOpen) return null;

  // ─── Selection helpers (single row only) ───
  const deselectAll = () => setSelectedIds(new Set());

  // ─── Clone handler (batched for performance) ───
  async function handleClone() {
    if (!sourceView || cloning || selectedIds.size === 0) return;

    const rowsToClone = sourceRows.filter(r => selectedIds.has(r.id));
    setCloning(true);
    setResult(null);
    setProgress({ done: 0, total: rowsToClone.length });

    try {
      let success = 0;
      let errors = 0;

      // Group selected rows by category, preserve cue order
      const categoryMap = new Map();
      rowsToClone.forEach(item => {
        const cat = String(item.category || "").trim();
        if (!cat) return;
        if (!categoryMap.has(cat)) categoryMap.set(cat, []);
        categoryMap.get(cat).push(item);
      });
      categoryMap.forEach(arr => arr.sort((a, b) => (a.cue || 0) - (b.cue || 0)));

      // Build ALL payloads upfront (no API calls yet)
      const allPayloads = [];
      for (const [category, items] of categoryMap) {
        let cue = 1;
        for (const item of items) {
          const payload = {
            name: item.name || "",
            clientSponsorName: item.clientSponsorName || "",
            notes: item.notes || "",
            clubInstructions: includeInstructions ? (item.clubInstructions || "") : "",
            fixture: fixtureForWrites,
            category,
            cue: cue++,
            contentStatus: defaultStatus,
            allocationId: item.allocationId || "",
            rotations: item.rotations || null,
            bonusPriority: item.bonusPriority || null,
            playerNumber: item.playerNumber || "",
            selectTiming: item.selectTiming || [],
            selectLocation: item.selectLocation || [],
            venueFlowEditor: editorName,
          };

          if (includeAssets) {
            for (const field of ASSET_FIELDS) {
              payload[field] = item[field] || null;
            }
          } else {
            for (const field of ASSET_FIELDS) {
              payload[field] = null;
            }
          }

          allPayloads.push(payload);
        }
      }

      // Create rows in batches of 5 (balances speed vs rate limits)
      // Airtable supports 10 per batch request but we use individual api.create
      // calls to go through the field-building pipeline. 5 concurrent promises
      // gives ~5x speedup without hammering the rate limit.
      const BATCH_SIZE = 5;
      const createdRows = [];

      for (let i = 0; i < allPayloads.length; i += BATCH_SIZE) {
        const batch = allPayloads.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map(payload => api.create(payload))
        );

        for (const result of results) {
          if (result.status === "fulfilled") {
            success++;
            if (result.value?.id) createdRows.push(result.value);
          } else {
            console.error("[CloneMatch] Failed to create row:", result.reason);
            errors++;
          }
        }

        setProgress({ done: Math.min(i + BATCH_SIZE, allPayloads.length), total: allPayloads.length });

        // Small delay between batches to avoid rate limits
        if (i + BATCH_SIZE < allPayloads.length) {
          await new Promise(r => setTimeout(r, 200));
        }
      }

      // Operator sync — batch after all creates are done (non-blocking)
      if (onSyncRow && includeAssets && createdRows.length > 0) {
        console.log(`[CloneMatch] Starting deferred operator sync for ${createdRows.length} rows...`);
        // Run sync in background — don't block the UI or the completion callback
        (async () => {
          let synced = 0;
          for (const created of createdRows) {
            try {
              const matchingPayload = allPayloads.find(p =>
                p.name === (created.fields?.Name || created.fields?.name || "")
              ) || {};
              const createdItem = { ...matchingPayload, id: created.id };
              await onSyncRow(created.id, createdItem);
              synced++;
            } catch (syncErr) {
              console.warn('[CloneMatch] Operator sync failed for:', created.id, syncErr);
            }
          }
          console.log(`[CloneMatch] Operator sync complete: ${synced}/${createdRows.length}`);
        })();
      }

      setResult({ success, errors, message: null });
      if (onComplete) onComplete();
    } catch (err) {
      console.error("[CloneMatch] Clone failed:", err);
      setResult({ success: 0, errors: 1, message: "Clone failed: " + (err.message || err) });
    } finally {
      setCloning(false);
    }
  }

  function handleClose() {
    if (cloning) return;
    setSourceView("");
    setSourceRows([]);
    setSelectedIds(new Set());
    setResult(null);
    setProgress({ done: 0, total: 0 });
    setLoadError(null);
    onClose();
  }

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  // ─── Styles ───
  const S = {
    overlay: {
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
    },
    modal: {
      background: "#1a1a1d", borderRadius: 16, padding: "28px 32px",
      minWidth: 520, maxWidth: 620, width: "90%",
      maxHeight: "85vh", display: "flex", flexDirection: "column",
      border: "1px solid rgba(255,255,255,0.1)",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    },
    label: { display: "block", fontSize: 12, fontWeight: 600, color: "#cbd5e1", marginBottom: 6 },
    select: {
      width: "100%", padding: "10px 12px", fontSize: 13,
      borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
      background: "#111114", color: "#f8fafc",
      cursor: cloning ? "not-allowed" : "pointer",
      marginBottom: 16, fontFamily: "inherit",
    },
    checkbox: { width: 16, height: 16, accentColor: "#22c55e", cursor: "pointer", flexShrink: 0 },
    rowList: {
      flex: 1, overflowY: "auto", marginBottom: 16,
      border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8,
      maxHeight: 320, minHeight: 100,
    },
    catHeader: {
      position: "sticky", top: 0, zIndex: 1,
      padding: "8px 12px", fontSize: 11, fontWeight: 700,
      color: "#94a3b8", background: "#111114",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      textTransform: "uppercase", letterSpacing: "0.04em",
      display: "flex", alignItems: "center", gap: 8,
      cursor: "pointer",
    },
    rowItem: (selected) => ({
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 12px 8px 28px", cursor: "pointer",
      background: selected ? "rgba(34,197,94,0.06)" : "transparent",
      borderBottom: "1px solid rgba(255,255,255,0.04)",
      transition: "background 0.1s ease",
    }),
    rowName: { fontSize: 13, fontWeight: 500, color: "#e2e8f0", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    rowMeta: { fontSize: 11, color: "#6b7280" },
    selectBar: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "8px 0", marginBottom: 8,
    },
    pillBtn: (active) => ({
      padding: "4px 12px", borderRadius: 4, fontSize: 11, fontWeight: 600,
      border: `1px solid ${active ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.12)"}`,
      background: active ? "rgba(34,197,94,0.1)" : "transparent",
      color: active ? "#22c55e" : "#94a3b8",
      cursor: "pointer",
    }),
    venueTag: {
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 10, fontWeight: 700, marginLeft: 8,
      background: "rgba(47,214,181,0.1)", color: "#2fd6b5",
      border: "1px solid rgba(47,214,181,0.2)",
    },
  };

  return (
    <div style={S.overlay} onClick={cloning ? undefined : handleClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>📋</span>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#f8fafc" }}>
              Clone
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#8a8a96" }}>
              Select a row from a previous match to copy into this match
              {currentVenue !== "ALL" && <span style={S.venueTag}>{currentVenue === "MRV" ? "Marvel" : currentVenue} only</span>}
            </p>
          </div>
        </div>

        {/* Source picker */}
        <label style={S.label}>Source Match</label>
        <select
          value={sourceView}
          onChange={e => setSourceView(e.target.value)}
          disabled={cloning}
          style={S.select}
        >
          <option value="">Select a match to clone from…</option>
          {sourceOptions.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        {/* Loading indicator */}
        {loadingRows && (
          <div style={{ textAlign: "center", padding: "20px 0", fontSize: 13, color: "#8a8a96" }}>
            Loading rows from {sourceView}…
          </div>
        )}

        {/* Load error */}
        {loadError && !loadingRows && (
          <div style={{
            padding: "10px 14px", borderRadius: 8, marginBottom: 16,
            background: "rgba(239,68,68,0.1)", borderLeft: "3px solid #ef4444",
            fontSize: 12, color: "#fca5a5", lineHeight: 1.5,
          }}>
            {loadError}
          </div>
        )}

        {/* Row selection */}
        {sourceRows.length > 0 && !loadingRows && (
          <>
            {/* Select bar */}
            <div style={S.selectBar}>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                {selectedIds.size === 0 
                  ? "Select a row to clone (one row at a time)" 
                  : <><strong style={{ color: "#e2e8f0" }}>1</strong> row selected</>}
              </span>
              {selectedIds.size > 0 && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={deselectAll} style={S.pillBtn(false)}>Clear</button>
                </div>
              )}
            </div>

            {/* Scrollable row list grouped by category */}
            <div style={S.rowList}>
              {Array.from(groupedRows.entries()).map(([cat, rows]) => {
                return (
                  <div key={cat}>
                    <div style={S.catHeader}>
                      {cat}
                      <span style={{ fontWeight: 400, opacity: 0.6 }}>
                        ({rows.length})
                      </span>
                    </div>
                    {rows.map(row => {
                      const selected = selectedIds.has(row.id);
                      const hasAsset = ASSET_FIELDS.some(f => row[f]);
                      return (
                        <div
                          key={row.id}
                          style={S.rowItem(selected)}
                          onClick={() => !cloning && setSelectedIds(new Set([row.id]))}
                        >
                          <input
                            type="radio"
                            name="cloneRow"
                            checked={selected}
                            onChange={() => setSelectedIds(new Set([row.id]))}
                            disabled={cloning}
                            style={S.checkbox}
                          />
                          <span style={S.rowName}>{row.name || "(untitled)"}</span>
                          {row.clientSponsorName && (
                            <span style={S.rowMeta}>{row.clientSponsorName}</span>
                          )}
                          {hasAsset && (
                            <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: "rgba(132,204,22,0.1)", color: "#84cc16", border: "1px solid rgba(132,204,22,0.2)" }}>
                              asset
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Options */}
        {sourceRows.length > 0 && !loadingRows && (
          <div style={{ display: "flex", gap: 16, marginBottom: 16, flexShrink: 0 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#e2e8f0", cursor: "pointer" }}>
              <input type="checkbox" checked={includeAssets} onChange={e => setIncludeAssets(e.target.checked)} disabled={cloning} style={S.checkbox} />
              <span style={{ fontWeight: 600 }}>Include assets</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#e2e8f0", cursor: "pointer" }}>
              <input type="checkbox" checked={includeInstructions} onChange={e => setIncludeInstructions(e.target.checked)} disabled={cloning} style={S.checkbox} />
              <span style={{ fontWeight: 600 }}>Include instructions</span>
            </label>
          </div>
        )}

        {/* Warning for selected rows with assets */}
        {selectedIds.size > 0 && includeAssets && !cloning && !result && (
          <div style={{
            padding: "10px 14px", borderRadius: 8, marginBottom: 16, flexShrink: 0,
            background: "rgba(251,191,36,0.08)", borderLeft: "3px solid #fbbf24",
            fontSize: 12, color: "#fcd34d", lineHeight: 1.5,
          }}>
            Review your selection carefully — deselect any match-specific content (e.g. numbered items, one-off activations) that shouldn't carry over to the new round.
          </div>
        )}

        {/* Progress */}
        {cloning && (
          <div style={{ marginBottom: 16, flexShrink: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#8a8a96", marginBottom: 4 }}>
              <span>Cloning selected rows…</span>
              <span>{progress.done} / {progress.total}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "#2a2a30", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 3,
                width: `${pct}%`,
                background: "linear-gradient(90deg, #22c55e, #22BA91)",
                transition: "width 0.3s ease",
              }} />
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={{
            padding: "12px 14px", borderRadius: 8, marginBottom: 16, flexShrink: 0,
            background: result.errors > 0 ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)",
            borderLeft: `3px solid ${result.errors > 0 ? "#ef4444" : "#22c55e"}`,
            fontSize: 12, color: result.errors > 0 ? "#fca5a5" : "#86efac", lineHeight: 1.5,
          }}>
            {result.message ? result.message : (
              <>
                <strong>{result.success} row{result.success !== 1 ? "s" : ""} cloned successfully.</strong>
                {result.errors > 0 && ` ${result.errors} failed.`}
              </>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button
            onClick={handleClose}
            disabled={cloning}
            style={{
              padding: "9px 20px", fontSize: 13, fontWeight: 600, borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
              color: "#94a3b8", cursor: cloning ? "not-allowed" : "pointer",
            }}
          >
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={handleClone}
              disabled={!sourceView || cloning || selectedIds.size === 0}
              style={{
                padding: "9px 24px", fontSize: 13, fontWeight: 700, borderRadius: 8,
                border: "none",
                background: (!sourceView || cloning || selectedIds.size === 0) ? "#2a2a30" : "linear-gradient(135deg, #22c55e, #16a34a)",
                color: (!sourceView || cloning || selectedIds.size === 0) ? "#6b6b78" : "#fff",
                cursor: (!sourceView || cloning || selectedIds.size === 0) ? "not-allowed" : "pointer",
                boxShadow: sourceView && !cloning && selectedIds.size > 0 ? "0 2px 10px rgba(34,197,94,0.3)" : "none",
              }}
            >
              {cloning ? `Cloning… ${pct}%` : `Clone Row`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
